import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormLabel,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  SimpleGrid,
  Switch,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { getMonitoringAlertSettings, updateMonitoringAlertSettings } from '../store/api_funcs';
import { getMonitoringDevices } from '../store/api_funcs';
import { useStores } from '../store/store_context';

const EVENT_LABELS = [
  ['offline', 'Панель офлайн'],
  ['temperature', 'Температура'],
  ['cpu', 'CPU'],
  ['disk', 'Диск'],
  ['memory', 'ОЗУ'],
  ['screenshot', 'Скриншоты'],
  ['error', 'Ошибки панели'],
  ['logs', 'Ошибки в логах'],
];

const NUMBER_FIELDS = [
  ['check_interval_sec', 'Проверять каждые, сек', 15, 3600],
  ['offline_minutes', 'Офлайн после, мин', 1, 1440],
  ['min_repeat_minutes', 'Минимум между повторами, мин', 1, 1440],
  ['warning_repeat_minutes', 'Повтор предупреждений, мин', 1, 1440],
  ['problem_repeat_minutes', 'Повтор проблем, мин', 1, 1440],
];

const THRESHOLD_FIELDS = [
  ['temperature_warning', 'Темп. предупреждение, °C'],
  ['temperature_problem', 'Темп. проблема, °C'],
  ['cpu_warning', 'CPU предупреждение, %'],
  ['cpu_problem', 'CPU проблема, %'],
  ['disk_warning', 'Диск предупреждение, %'],
  ['disk_problem', 'Диск проблема, %'],
  ['memory_warning_gb', 'ОЗУ предупреждение, ГБ свободно'],
  ['memory_problem_gb', 'ОЗУ проблема, ГБ свободно'],
  ['screenshot_warning_minutes', 'Скрин устарел, мин'],
  ['screenshot_problem_minutes', 'Скрина нет, мин'],
];

const cardProps = {
  border: '1px solid rgba(139,92,246,0.28)',
  borderRadius: '14px',
  bg: 'rgba(17, 18, 32, 0.82)',
  boxShadow: '0 18px 60px rgba(0,0,0,0.22)',
};

const fieldBg = {
  bg: 'rgba(9, 12, 23, 0.72)',
  borderColor: 'rgba(255,255,255,0.14)',
  color: 'rgba(255,255,255,0.92)',
  _hover: { borderColor: 'rgba(139,92,246,0.58)' },
  _focus: { borderColor: '#8B5CF6', boxShadow: '0 0 0 1px #8B5CF6' },
};

const MonitoringAlertsPage = observer(() => {
  const { pageStore } = useStores();
  const toast = useToast();
  const [settings, setSettings] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const activeCount = useMemo(() => {
    if (!settings?.types) return 0;
    return Object.values(settings.types).filter(Boolean).length;
  }, [settings]);

  const load = async () => {
    setLoading(true);
    try {
      setSettings(await getMonitoringAlertSettings(pageStore.token));
      const devicesResult = await getMonitoringDevices(pageStore.token).catch(() => ({ devices: [] }));
      setDevices(devicesResult.devices || []);
    } catch (error) {
      toast({ status: 'error', title: 'Настройки уведомлений недоступны', description: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const patch = partial => setSettings(current => ({ ...current, ...partial }));
  const patchType = (key, value) =>
    setSettings(current => ({ ...current, types: { ...(current.types || {}), [key]: value } }));
  const patchThreshold = (key, value) =>
    setSettings(current => ({ ...current, thresholds: { ...(current.thresholds || {}), [key]: value } }));
  const patchPanel = (fabric, enabled) =>
    setSettings(current => ({ ...current, panels: { ...(current.panels || {}), [String(fabric)]: enabled } }));

  const save = async () => {
    setSaving(true);
    try {
      setSettings(await updateMonitoringAlertSettings(settings, pageStore.token));
      toast({ status: 'success', title: 'Настройки уведомлений сохранены' });
    } catch (error) {
      toast({ status: 'error', title: 'Не удалось сохранить настройки', description: error.message });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) {
    return (
      <Box {...cardProps} p={5}>
        <Text color="rgba(255,255,255,0.74)">{loading ? 'Загрузка настроек...' : 'Настройки не загружены'}</Text>
      </Box>
    );
  }

  return (
    <VStack align="stretch" spacing={4} color="white">
      <HStack justify="space-between" align="flex-end">
        <Box>
          <Heading size="lg">Уведомления мониторинга</Heading>
          <Text color="rgba(255,255,255,0.58)" mt={1}>
            Настройки Telegram-уведомлений по проблемам панелей. Активно типов: {activeCount}
          </Text>
        </Box>
        <HStack>
          <Button onClick={load} isLoading={loading} variant="outline" colorScheme="purple">
            Обновить
          </Button>
          <Button onClick={save} isLoading={saving} colorScheme="purple">
            Сохранить
          </Button>
        </HStack>
      </HStack>

      <Grid templateColumns={{ base: '1fr', xl: '0.85fr 1.15fr' }} gap={4}>
        <GridItem>
          <VStack align="stretch" spacing={4}>
            <Box {...cardProps} p={5}>
              <Heading size="md" mb={4}>Основное</Heading>
              <SimpleGrid columns={1} spacing={4}>
                <FormControl display="flex" justifyContent="space-between" alignItems="center">
                  <FormLabel m={0}>Уведомления включены</FormLabel>
                  <Switch colorScheme="purple" isChecked={settings.enabled} onChange={e => patch({ enabled: e.target.checked })} />
                </FormControl>
                <FormControl display="flex" justifyContent="space-between" alignItems="center">
                  <FormLabel m={0}>Отправлять в группу поддержки</FormLabel>
                  <Switch colorScheme="purple" isChecked={settings.support_chat_enabled} onChange={e => patch({ support_chat_enabled: e.target.checked })} />
                </FormControl>
              </SimpleGrid>
              <Divider my={4} borderColor="rgba(255,255,255,0.12)" />
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {NUMBER_FIELDS.map(([key, label, min, max]) => (
                  <FormControl key={key}>
                    <FormLabel color="rgba(255,255,255,0.62)" fontSize="sm">{label}</FormLabel>
                    <Input
                      type="number"
                      min={min}
                      max={max}
                      value={settings[key] ?? ''}
                      onChange={e => patch({ [key]: e.target.value })}
                      {...fieldBg}
                    />
                  </FormControl>
                ))}
              </SimpleGrid>
            </Box>

            <Box {...cardProps} p={5}>
              <Heading size="md" mb={4}>Типы событий</Heading>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                {EVENT_LABELS.map(([key, label]) => (
                  <Checkbox
                    key={key}
                    colorScheme="purple"
                    isChecked={Boolean(settings.types?.[key])}
                    onChange={e => patchType(key, e.target.checked)}
                  >
                    {label}
                  </Checkbox>
                ))}
              </SimpleGrid>
            </Box>

            <Box {...cardProps} p={5}>
              <Heading size="md" mb={1}>Панели</Heading>
              <Text color="rgba(255,255,255,0.55)" fontSize="sm" mb={4}>
                Отключение здесь полностью запрещает уведомления по выбранной панели.
              </Text>
              <VStack align="stretch" spacing={2} maxH="360px" overflowY="auto" pr={1}>
                {devices.map(device => {
                  const fabric = String(device.fabric_number || '');
                  const enabled = settings.panels?.[fabric] ?? true;
                  return (
                    <HStack
                      key={fabric || device.id}
                      justify="space-between"
                      border="1px solid rgba(255,255,255,0.08)"
                      borderRadius="10px"
                      p={3}
                      bg="rgba(9,12,23,0.48)"
                    >
                      <Box minW={0}>
                        <Text fontWeight={700} noOfLines={1}>{device.name || device.address || 'Панель'}</Text>
                        <Text color="rgba(255,255,255,0.52)" fontSize="sm">{fabric || 'без номера'}</Text>
                      </Box>
                      <Switch
                        colorScheme="purple"
                        isChecked={enabled}
                        isDisabled={!fabric}
                        onChange={e => patchPanel(fabric, e.target.checked)}
                      />
                    </HStack>
                  );
                })}
                {!devices.length ? (
                  <Text color="rgba(255,255,255,0.55)">Панели не загружены</Text>
                ) : null}
              </VStack>
            </Box>
          </VStack>
        </GridItem>

        <GridItem>
          <Box {...cardProps} p={5}>
            <Heading size="md" mb={4}>Пороги срабатывания</Heading>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              {THRESHOLD_FIELDS.map(([key, label]) => (
                <FormControl key={key}>
                  <FormLabel color="rgba(255,255,255,0.62)" fontSize="sm">{label}</FormLabel>
                  <Input
                    type="number"
                    step={key.includes('_gb') ? '0.1' : '1'}
                    value={settings.thresholds?.[key] ?? ''}
                    onChange={e => patchThreshold(key, e.target.value)}
                    {...fieldBg}
                  />
                </FormControl>
              ))}
            </SimpleGrid>
          </Box>
        </GridItem>
      </Grid>
    </VStack>
  );
});

export default MonitoringAlertsPage;
